// Canvas-based confetti burst animation controller.
// Fires a particle celebration effect when connected, and exposes a fire() action
// to trigger additional bursts on demand (e.g. a "Celebrate Again" button).
//
// If no canvas exists, one will be created dynamically when fire() is called.
import { Controller } from "@hotwired/stimulus"

const COLORS = ["#c9a84c","#1a2744","#e8f0fe","#f5e6c8","#4a7c59","#e8c96a","#7aa2d4","#f0d080","#ffffff","#d4a843","#3a5f8a"]
const SHAPES = ["rect","circle","ribbon"]

// Standalone confetti function that can be called without the controller
window.fireConfetti = function() {
  const canvas = document.createElement("canvas")
  canvas.id = "confetti-canvas"
  canvas.className = "position-fixed top-0 start-0 w-100 h-100 pointer-events-none"
  canvas.style.zIndex = "9999"
  document.body.appendChild(canvas)
  
  const ctx = canvas.getContext("2d")
  const W = canvas.width = window.innerWidth
  const H = canvas.height = window.innerHeight
  
  let particles = []
  let animId = null

  const rand = (a, b) => a + Math.random() * (b - a)
  const make = (x, y, burst) => ({
    x: x ?? rand(0, W), y: y ?? rand(-20, -200),
    vx: burst ? rand(-12, 12) : rand(-2, 2),
    vy: burst ? rand(-18, -4) : rand(2, 6),
    w: rand(6, 14), h: rand(3, 14), rot: Math.random() * Math.PI * 2,
    rotV: rand(-0.15, 0.15), alpha: 1, gravity: rand(0.12, 0.28), drag: rand(0.97, 0.995),
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    shape: SHAPES[Math.floor(Math.random() * SHAPES.length)]
  })

  const draw = p => {
    ctx.save(); ctx.globalAlpha = p.alpha; ctx.fillStyle = p.color
    ctx.translate(p.x, p.y); ctx.rotate(p.rot)
    p.shape === "circle" ? (ctx.beginPath(), ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2), ctx.fill()) : ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h)
    ctx.restore()
  }

  const tick = p => { p.vy += p.gravity; p.vx *= p.drag; p.vy *= p.drag; p.x += p.vx; p.y += p.vy; p.rot += p.rotV; if (p.y > H * 0.7) p.alpha -= 0.02; return p.alpha > 0 && p.y < H + 20 }

  const loop = () => {
    ctx.clearRect(0, 0, W, H)
    particles = particles.filter(p => { draw(p); return tick(p) })
    particles.length > 0 ? animId = requestAnimationFrame(loop) : (ctx.clearRect(0, 0, W, H), animId = null)
  }

  if (animId) cancelAnimationFrame(animId)
  particles = []
  const cx = W / 2
  for (let i = 0; i < 80; i++) particles.push(make(cx - 60, H * 0.45, true))
  for (let i = 0; i < 80; i++) particles.push(make(cx + 60, H * 0.45, true))
  for (let i = 0; i < 60; i++) particles.push(make(null, null, false))
  loop()

  let t = 0
  const rain = setInterval(() => { for (let i = 0; i < 8; i++) particles.push(make(null, null, false)); if ((t += 60) >= 1500) clearInterval(rain) }, 60)

  // Remove canvas after animation
  setTimeout(() => canvas.remove(), 3000)
}

export default class extends Controller {
  connect() {
    this.canvas = document.getElementById("confetti-canvas")
    if (!this.canvas) return
    this.setupCanvas()
    setTimeout(() => this.fire(), 300)
  }

  setupCanvas() {
    this.ctx = this.canvas.getContext("2d")
    this.W = this.canvas.width = window.innerWidth
    this.H = this.canvas.height = window.innerHeight
    this._resize = () => {
      this.W = this.canvas.width = window.innerWidth
      this.H = this.canvas.height = window.innerHeight
    }
    window.addEventListener("resize", this._resize)
  }

  disconnect() {
    window.removeEventListener("resize", this._resize)
  }

  ensureCanvas() {
    if (!this.canvas || !document.body.contains(this.canvas)) {
      this.canvas = document.createElement("canvas")
      this.canvas.id = "confetti-canvas"
      this.canvas.className = "position-fixed top-0 start-0 w-100 h-100 pointer-events-none"
      this.canvas.style.zIndex = "9999"
      document.body.appendChild(this.canvas)
      this.setupCanvas()
    }
    return this.canvas
  }

  fire() {
    // Call the global function instead of duplicating logic
    if (window.fireConfetti) {
      window.fireConfetti()
    }
  }
}
